import { useCallback, useEffect, useRef, useState } from "react";
import type { TermsRead } from "./operations";
import {
  acceptanceOf,
  NO_TERMS_PUBLISHED,
  sameTerms,
  type CurrentTerms,
  type TermsDecision,
  type TermsLocale,
  type TermsRefusal,
} from "./terms";

/** What the page can show about the Terms right now. */
export type TermsView =
  | { readonly status: "loading" }
  /** Backend says none are published: no checkbox, nothing sent. */
  | { readonly status: "none" }
  | { readonly status: "published"; readonly terms: CurrentTerms }
  /** Could not be read (or Backend could not read them at send). The 4.5 frame. */
  | { readonly status: "unavailable" };

export interface TermsConsent {
  readonly view: TermsView;
  /** Ticked, for exactly the Terms on screen now. */
  readonly accepted: boolean;
  setAccepted(checked: boolean): void;
  /** Read again. "Load terms again". */
  reload(): void;
  /**
   * Backend refused the acceptance. `stale` / `required`: untick and re-read,
   * so the person sees what is in force now and decides again. `unavailable`:
   * untick and show the "couldn't load the terms" frame; nothing is read until
   * the person asks.
   */
  refused(refusal: TermsRefusal): void;
  /**
   * The decision a send may carry, or null when there is none to carry: still
   * loading, unreadable, or published and not ticked. Callers send nothing on
   * null.
   */
  decision(): TermsDecision | null;
  /** The document on screen, for links; null when there is none. */
  readonly documentUrl: string | null;
}

/**
 * THE TERMS ON ONE SCREEN, AND WHETHER THE PERSON AGREED TO THEM.
 *
 * `read` is injected (the booking API on Review, the account API on account
 * creation), and so are the locale and whether the screen is showing at all.
 *
 * The tick is stored AS THE TERMS IT WAS GIVEN FOR, not as a boolean. A tick
 * therefore cannot outlive the Terms it agreed to: a re-read that returns a
 * new version, or a language switch that returns the Spanish text, unticks by
 * construction. A refusal unticks explicitly as well, because TERMS_STALE can
 * come back with the same answer (a publication between two reads).
 *
 * THE TICK IS A FRESH ACT EACH TIME THE SCREEN IS SHOWN (security audit
 * WEB-TOS-01). The hook lives with Book across every step, so leaving Review,
 * a new hold or another professional (`scope`) clears the tick: coming back
 * to Review shows the box unticked, and nothing can be sent on an agreement
 * given for a different appointment.
 *
 * AFTER A STALE OR REQUIRED REFUSAL, "NONE" IS NOT BELIEVED (audit
 * residual). Backend just said Terms are in force, so a read that now says
 * none are published, or that the field is missing, is a split rollout
 * talking, and is shown as unreadable until a published answer arrives.
 * Otherwise the page could drop the box and send nothing, forever.
 *
 * Reads are fenced by a generation number, so a slow answer for English that
 * arrives after the person switched to Spanish is dropped rather than shown.
 */
export function useTermsConsent({
  read,
  locale,
  enabled,
  scope = "",
  refusal = null,
}: {
  read: (locale: TermsLocale) => Promise<TermsRead>;
  locale: TermsLocale;
  enabled: boolean;
  /** What the tick is for (e.g. professional and hold). A change clears it. */
  scope?: string;
  /**
   * Backend's latest refusal of the Terms a send carried, as the object the
   * caller's state holds (a new object per refusal), or null. The tick is
   * cleared in the SAME render that first sees it, so no committed frame can
   * offer the old agreement for another send (reviewer P3); the re-read or
   * the 4.5 frame follows in an effect.
   */
  refusal?: { readonly refusal: TermsRefusal } | null;
}): TermsConsent {
  const [view, setView] = useState<TermsView>({ status: "loading" });
  const [acceptedFor, setAcceptedFor] = useState<CurrentTerms | null>(null);
  /** Bumped to ask for a fresh read; the effect below does the reading. */
  const [readRequest, setReadRequest] = useState(0);
  const generation = useRef(0);
  /** Set by a stale or required refusal; cleared by a published answer. */
  const distrustNone = useRef(false);
  /**
   * The last READ failed (as opposed to a refusal's "unavailable" frame, which
   * waits for the person). A reconnect retries it once by itself: on WebKit a
   * read that failed offline otherwise stayed failed until a tap (QE, rc.7).
   */
  const readFailed = useRef(false);

  /*
   * THE TICK GOES IN THE SAME RENDER as the change that voids it: leaving the
   * screen, a different appointment (`scope`), or a new refusal. React's
   * "adjust state when a prop changes" pattern: the state is set during
   * render behind a guard, and React re-renders before committing, so no
   * frame shows (or can send) the old agreement. An effect would leave one.
   */
  const tickKey = `${enabled ? "on" : "off"}|${scope}`;
  const [tickFor, setTickFor] = useState(tickKey);
  /*
   * The last refusal ACTED ON, kept across a null between. A press that a
   * policy stop halts passes through "sending" and restores the same failure
   * (rc.10 `useBooking.commit`), so the caller's refusal goes F → null → F.
   * That F is no new answer from Backend: the tick given since, for the Terms
   * on screen, stands (rc.11 F1, the Web Director). Only a NEW refusal unticks.
   */
  const [seenRefusal, setSeenRefusal] = useState(refusal);
  const freshRefusal = refusal !== null && refusal !== seenRefusal;
  // `voided` also answers for THIS pass, which React discards and re-runs:
  // not even the discarded pass may report the old agreement.
  let voided = false;
  if (tickFor !== tickKey || freshRefusal) {
    setTickFor(tickKey);
    if (freshRefusal) setSeenRefusal(refusal);
    setAcceptedFor(null);
    voided = true;
  }

  useEffect(() => {
    if (!enabled) return;
    const mine = ++generation.current;
    setView({ status: "loading" });
    void read(locale).then((result) => {
      if (mine !== generation.current) return;
      readFailed.current = false;
      if (result.kind === "published") {
        distrustNone.current = false;
        setView({ status: "published", terms: result.terms });
      } else if (result.kind === "none" && !distrustNone.current) setView({ status: "none" });
      else {
        readFailed.current = true;
        setView({ status: "unavailable" });
      }
    });
    return () => {
      // A read still in flight when the screen goes away (or the locale
      // changes) must not land.
      generation.current += 1;
    };
  }, [enabled, locale, read, readRequest]);

  const reload = useCallback(() => {
    setAcceptedFor(null);
    setReadRequest((n) => n + 1);
  }, []);

  const refused = useCallback((kind: TermsRefusal) => {
    setAcceptedFor(null);
    if (kind !== "unavailable") distrustNone.current = true;
    if (kind === "unavailable") {
      // Stop any read in flight: the frame stays until the person asks.
      generation.current += 1;
      readFailed.current = false;
      setView({ status: "unavailable" });
      return;
    }
    setReadRequest((n) => n + 1);
  }, []);

  // A failed read retries once the browser is back online; a refusal's frame
  // does not (it waits for the person).
  useEffect(() => {
    if (!enabled || view.status !== "unavailable") return;
    const onOnline = () => {
      if (readFailed.current) reload();
    };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [enabled, view.status, reload]);

  // The re-read, or the 4.5 frame, for a refusal the caller's state reports:
  // once per refusal, not again when the same one comes back (F1, above).
  const actedOn = useRef<typeof refusal>(null);
  useEffect(() => {
    if (!refusal || refusal === actedOn.current) return;
    actedOn.current = refusal;
    refused(refusal.refusal);
  }, [refusal, refused]);

  const shown = view.status === "published" ? view.terms : null;
  const accepted =
    !voided && shown !== null && acceptedFor !== null && sameTerms(acceptanceOf(acceptedFor), acceptanceOf(shown));

  const setAccepted = useCallback(
    (checked: boolean) => setAcceptedFor(checked ? shown : null),
    [shown]
  );

  const decision = (): TermsDecision | null => {
    if (view.status === "none") return NO_TERMS_PUBLISHED;
    if (view.status === "published" && accepted) return Object.freeze({ acceptance: acceptanceOf(view.terms) });
    return null;
  };

  return {
    view,
    accepted,
    setAccepted,
    reload,
    refused,
    decision,
    documentUrl: shown ? shown.documentUrl : null,
  };
}
